import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { MapPin, Star, Languages, Calendar, MessageCircle, Phone, CheckCircle2, Plus, Minus, AlertCircle, Clock, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-1391">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-1392" />
    </svg>;
}
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-1393">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-1394">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-1395">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-1396">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-1397" /> : <Plus className="w-4 h-4" data-id="element-1398" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-1399">
        <p className="text-gray-600 leading-relaxed" data-id="element-1400">{answer}</p>
      </div>
    </div>;
}
export function EmergencyDentistLondonRuPage() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Что делать, если сильно болит зуб?',
    a: 'Как можно скорее свяжитесь с нами для неотложного осмотра. Мы определим причину боли и предложим лечение. Не грейте больное место!'
  }, {
    q: 'Принимаете ли вы срочно в тот же день?',
    a: 'Да, мы всегда стараемся найти время для пациентов с острой болью в тот же день. Напишите в WhatsApp/Telegram или позвоните.'
  }, {
    q: 'Сколько стоит экстренный прием?',
    a: 'Стоимость зависит от необходимых процедур и диагностики. Экстренная консультация — £60. После осмотра мы объясним варианты лечения и цены.'
  }, {
    q: 'Какие симптомы считаются неотложными?',
    a: 'Сильная зубная боль, отек лица или десен, гной, повышенная температура, боль при глотании, травма зуба (скол, вывих), кровотечение после удаления.'
  }, {
    q: 'Что делать, если выбили зуб?',
    a: 'Найдите зуб, держите его за коронку (не за корень!), промойте водой, если грязный. Попробуйте вставить обратно в лунку или положите в молоко/слюну. Обратитесь к врачу в течение 30-60 минут!'
  }, {
    q: 'Работаете ли вы в выходные?',
    a: 'Мы работаем с понедельника по субботу. В неотложных случаях напишите нам в WhatsApp, мы попробуем помочь или посоветовать.'
  }, {
    q: 'Можно ли получить помощь на русском?',
    a: 'Да, в Dentico Clinic возможно общение на русском языке. Вы сможете точно описать свои симптомы.'
  }, {
    q: 'Где вы находитесь?',
    a: '360–364 City Road, London EC1V 2PY (Angel / Islington), рядом с Angel Station.'
  }];
  // Schemas
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: 'Emergency Dental Care',
    description: 'Urgent dental treatment for pain relief, trauma, or infection.',
    procedureType: 'http://schema.org/SurgicalProcedure',
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  };
  return <Layout data-id="element-1401">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-1402" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-1403" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(procedureSchema)
    }} data-id="element-1404" />

      {/* 1. HERO SECTION (Urgent styling) */}
      <section className="relative bg-white overflow-hidden" data-id="element-1405">
        <div className="absolute inset-0 bg-red-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-1406" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-1407">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1408">
            <div className="space-y-6" data-id="element-1409">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 text-red-800 text-sm font-medium animate-pulse" data-id="element-1410">
                <AlertCircle className="w-4 h-4" data-id="element-1411" />
                Неотложная помощь
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-1412">
                Неотложная стоматологическая помощь в Лондоне –{' '}
                <span className="text-red-600" data-id="element-1413">Dentico Clinic</span>
                <br data-id="element-1414" />
                <span className="text-2xl sm:text-3xl font-medium text-gray-500 mt-2 block" data-id="element-1415">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-1416">
                Острая боль? Сломанный зуб? Отек? Мы принимаем пациентов с
                острой болью в тот же день. Консультация на русском языке.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-gray-700" data-id="element-1417">
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-1418">
                  <Clock className="w-4 h-4 text-red-600" data-id="element-1419" />
                  Срочный приём
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-1420">
                  <MapPin className="w-4 h-4 text-teal-600" data-id="element-1421" />
                  Angel, EC1V
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-1422">
                  <Languages className="w-4 h-4 text-teal-600" data-id="element-1423" />
                  Русский язык
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4" data-id="element-1424">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1425">
                  <Button size="lg" className="w-full gap-2 bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/20" data-id="element-1426">
                    <MessageCircle className="w-5 h-5" data-id="element-1427" />
                    Написать в WhatsApp
                  </Button>
                </a>
                <a href="tel:+447345197820" className="w-full sm:w-auto" data-id="element-1428">
                  <Button variant="outline" size="lg" className="w-full gap-2 border-red-200 text-red-700 hover:bg-red-50" data-id="element-1429">
                    <Phone className="w-5 h-5" data-id="element-1430" />
                    +44 7345 197820
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-1431">
              <div className="absolute inset-0 bg-red-100 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-1432"></div>
              <img src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&w=1200&q=80" alt="Неотложная стоматология Лондон" className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full grayscale-[20%]" data-id="element-1433" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHEN TO SEEK HELP */}
      <section className="py-16 bg-white" data-id="element-1434">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1435">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1436">
            Когда нужна неотложная помощь?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1437">
            {[{
            title: 'Сильная зубная боль',
            desc: 'Боль, которая не проходит от обезболивающих, пульсирующая или ночная боль.'
          }, {
            title: 'Отек лица',
            desc: 'Припухлость щеки или десны может свидетельствовать о серьезной инфекции.'
          }, {
            title: 'Травма зуба',
            desc: 'Сломанный, сколотый или выбитый зуб в результате удара или падения.'
          }, {
            title: 'Потеря пломбы/коронки',
            desc: 'Если выпала пломба или коронка, и зуб стал чувствительным или острым.'
          }, {
            title: 'Кровотечение',
            desc: 'Кровотечение из десны после удаления зуба, которое не останавливается.'
          }, {
            title: 'Боль при глотании',
            desc: 'Затрудненное глотание или дыхание — это признак опасного распространения инфекции.'
          }].map((item, idx) => <Card key={idx} className="p-6 border-l-4 border-l-red-500" data-id="element-1438">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-1439">
                  <AlertCircle className="w-5 h-5 text-red-500" data-id="element-1440" />
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-1441">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 3. FIRST AID */}
      <section className="py-16 bg-red-50" data-id="element-1442">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1443">
          <div className="text-center mb-10" data-id="element-1444">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1445">
              Что делать до визита к врачу?
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1446">
              Советы первой помощи, пока вы ждете приема.
            </p>
          </div>
          <div className="space-y-4" data-id="element-1447">
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-1448">
              <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-1449">
                При зубной боли
              </h3>
              <p className="text-gray-600" data-id="element-1450">
                Примите обезболивающее (ибупрофен или парацетамол), прополощите
                рот теплой водой. Не прикладывайте аспирин к десне и не грейте
                больное место!
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-1451">
              <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-1452">
                Если выбили зуб
              </h3>
              <p className="text-gray-600" data-id="element-1453">
                Держите зуб только за коронку (белую часть). Если грязный —
                промойте молоком или водой. Попробуйте вставить обратно в лунку
                или положите в молоко/слюну. Время критично — у вас есть 30-60
                минут!
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-1454">
              <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-1455">
                При отеке
              </h3>
              <p className="text-gray-600" data-id="element-1456">
                Приложите холодный компресс к щеке снаружи. Это уменьшит отек и
                боль. Не используйте тепло!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. OUR PROCESS */}
      <section className="py-16 bg-white" data-id="element-1457">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1458">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1459">
            Как мы работаем в экстренных случаях
          </h2>
          <div className="grid md:grid-cols-4 gap-8" data-id="element-1460">
            {[{
            step: '01',
            title: 'Связь',
            desc: 'Напишите нам в WhatsApp или позвоните. Опишите симптомы.'
          }, {
            step: '02',
            title: 'Оценка',
            desc: 'Мы оценим срочность и найдем ближайшее время для визита.'
          }, {
            step: '03',
            title: 'Визит',
            desc: 'Осмотр, рентген-диагностика и снятие острой боли.'
          }, {
            step: '04',
            title: 'План',
            desc: 'Если требуется дальнейшее лечение, мы составим план действий.'
          }].map((item, idx) => <Card key={idx} className="p-6 relative overflow-hidden" data-id="element-1461">
                <div className="text-6xl font-bold text-red-50 absolute -top-4 -right-4 select-none" data-id="element-1462">
                  {item.step}
                </div>
                <div className="relative z-10" data-id="element-1463">
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1464">
                    {item.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-1465">{item.desc}</p>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 5. COMMON TREATMENTS */}
      <section className="py-16 bg-gray-50" data-id="element-1466">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1467">
          <div className="text-center mb-12" data-id="element-1468">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1469">
              Виды неотложной помощи
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1470">
            {[{
            title: 'Снятие острой боли',
            desc: 'Медикаментозная обработка и временная пломба.'
          }, {
            title: 'Удаление зуба',
            desc: 'Если зуб невозможно спасти или он является источником инфекции.'
          }, {
            title: 'Лечение каналов',
            desc: 'Удаление воспаленного нерва (пульпэктомия) для спасения зуба.'
          }, {
            title: 'Фиксация коронки',
            desc: 'Цементирование выпавшей коронки или моста.'
          }, {
            title: 'Вскрытие абсцесса',
            desc: 'Дренаж гноя для снятия отека и боли.'
          }, {
            title: 'Шинирование зубов',
            desc: 'Фиксация подвижных зубов после травмы.'
          }].map((item, idx) => <Card key={idx} className="p-6 bg-white" data-id="element-1471">
                <Zap className="w-8 h-8 text-red-500 mb-4" data-id="element-1472" />
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1473">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-1474">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-16 bg-white" data-id="element-1475">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1476">
          <div className="text-center mb-10" data-id="element-1477">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1478">
              Частые вопросы
            </h2>
          </div>
          <div className="space-y-2" data-id="element-1479">
            {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-1480" />)}
          </div>
        </div>
      </section>

      {/* 7. INTERNAL LINKS */}
      <section className="py-12 bg-gray-50 border-t border-gray-100" data-id="element-1481">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1482">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1483">
            Смотрите также
          </h2>
          <div className="flex flex-wrap gap-4" data-id="element-1484">
            <Link to="/ru/russkij-stomatolog-london" data-id="element-1485">
              <Button variant="outline" size="sm" data-id="element-1486">
                Русскоязычный стоматолог
              </Button>
            </Link>
            <Link to="/ru/lechenie-kanalov-london" data-id="element-1487">
              <Button variant="outline" size="sm" data-id="element-1488">
                Лечение каналов
              </Button>
            </Link>
            <Link to="/ru/services/extraction" data-id="element-1489">
              <Button variant="outline" size="sm" data-id="element-1490">
                Удаление зубов
              </Button>
            </Link>
            <Link to="/ru/prices" data-id="element-1491">
              <Button variant="outline" size="sm" data-id="element-1492">
                Цены
              </Button>
            </Link>
            <Link to="/ru/contact" data-id="element-1493">
              <Button variant="outline" size="sm" data-id="element-1494">
                Контакты
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. CTA (Urgent) */}
      <section className="py-16 bg-red-900" data-id="element-1495">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1496">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-1497">
            Нужна срочная помощь?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto" data-id="element-1498">
            Не ждите, пока боль усилится. Свяжитесь с нами прямо сейчас.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1499">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1500">
              <Button size="lg" className="w-full gap-2 bg-green-600 hover:bg-green-700 text-white border-none" data-id="element-1501">
                <MessageCircle className="w-5 h-5" data-id="element-1502" />
                WhatsApp
              </Button>
            </a>
            <a href="tel:+447345197820" className="w-full sm:w-auto" data-id="element-1503">
              <Button variant="outline" size="lg" className="w-full gap-2 border-white text-white hover:bg-white/10" data-id="element-1504">
                <Phone className="w-5 h-5" data-id="element-1505" />
                +44 7345 197820
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>;
}